import type { FC, ReactNode } from 'react'
import type { OutputFormat } from '@/lib/image-tools'

import { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { Download } from 'lucide-react'
import {
    ChoiceGroup,
    Field,
    FilePicker,
    Preview,
    PrivacyNote,
    Stats,
    Status,
    errorMessage,
    inputClass,
    primaryButton,
    secondaryButton
} from '@/components/tools/tool-ui'
import {
    FORMAT_LABEL,
    IMAGE_ACCEPT,
    MIME,
    downloadBlob,
    encodeCanvas,
    fileStem,
    formatBytes,
    openImage,
    renderImage
} from '@/lib/image-tools'

const FORMATS: OutputFormat[] = ['jpg', 'png', 'webp']

const FORMAT_HELP: Record<OutputFormat, string> = {
    jpg: 'Best for photos. Works almost everywhere. No transparency.',
    png: 'Best for logos, screenshots and graphics with text. Keeps transparency. Files can be large for photos.',
    webp: 'Small files for websites. Keeps transparency. Some older apps and upload forms do not accept it.'
}

const ConvertTool: FC = (): ReactNode => {
    const router = useRouter()
    const [file, setFile] = useState<File | null>(null)
    const [img, setImg] = useState<HTMLImageElement | null>(null)
    const [preview, setPreview] = useState<Blob | null>(null)
    const [to, setTo] = useState<OutputFormat>('jpg')
    const [quality, setQuality] = useState(90)
    const [background, setBackground] = useState('#ffffff')
    const [result, setResult] = useState<Blob | null>(null)
    const [status, setStatus] = useState<{ tone: 'idle' | 'working' | 'success' | 'error', text: string }>({ tone: 'idle', text: 'Choose an image to begin.' })

    useEffect(() => {
        if (!router.isReady) return
        const query = String(router.query.to ?? '').toLowerCase()
        if (query === 'jpg' || query === 'jpeg') setTo('jpg')
        if (query === 'png' || query === 'webp') setTo(query)
    }, [router.isReady, router.query.to])

    const handleFiles = async ([next]: File[]) => {
        setResult(null)
        setStatus({ tone: 'working', text: 'Opening your image.' })
        try {
            const opened = await openImage(next)
            setFile(next)
            setImg(opened.img)
            setPreview(opened.blob)
            setStatus({ tone: 'idle', text: 'Ready. Pick a format and convert.' })
        }
        catch (error) {
            setStatus({ tone: 'error', text: errorMessage(error) })
        }
    }

    const handleConvert = async () => {
        if (!img) return
        setStatus({ tone: 'working', text: `Converting to ${FORMAT_LABEL[to]}.` })
        try {
            const canvas = renderImage(img, {
                width: img.naturalWidth,
                height: img.naturalHeight,
                background: to === 'jpg' ? background : undefined
            })
            const blob = await encodeCanvas(canvas, MIME[to], to === 'png' ? undefined : quality / 100)
            setResult(blob)
            setPreview(blob)
            setStatus({ tone: 'success', text: `Done. Your ${FORMAT_LABEL[to]} is ready to save.` })
        }
        catch (error) {
            setStatus({ tone: 'error', text: errorMessage(error) })
        }
    }

    return (
        <div className='grid gap-8 lg:grid-cols-[1fr_1.1fr]'>
            <div className='space-y-6'>
                <FilePicker accept={IMAGE_ACCEPT} label={file ? 'Choose a different image' : 'Choose image'} hint={file ? file.name : 'JPG, PNG, WebP, HEIC, GIF and more'} onFiles={handleFiles} />

                <div>
                    <ChoiceGroup
                        label='Convert to'
                        value={to}
                        onChange={(value) => {
                            setTo(value)
                            setResult(null)
                        }}
                        options={FORMATS.map((format) => ({ value: format, label: FORMAT_LABEL[format] }))}
                    />
                    <p className='mt-2 text-xs text-slate-500'>{FORMAT_HELP[to]}</p>
                </div>

                {to !== 'png' && (
                    <Field label={`Quality: ${quality}%`} htmlFor='convert-quality'>
                        <input id='convert-quality' type='range' min={40} max={100} value={quality} onChange={(event) => setQuality(Number(event.target.value))} className='w-full accent-cyan-300' />
                    </Field>
                )}

                {to === 'jpg' && (
                    <Field label='Fill transparent areas with' htmlFor='convert-background'>
                        <div className='flex items-center gap-3'>
                            <input id='convert-background' type='color' value={background} onChange={(event) => setBackground(event.target.value)} className='h-11 w-16 cursor-pointer rounded-lg border border-white/10 bg-transparent' />
                            <input aria-label='Background color code' value={background} onChange={(event) => setBackground(event.target.value)} className={inputClass} />
                        </div>
                    </Field>
                )}

                <div className='flex flex-col gap-3 sm:flex-row'>
                    <button type='button' onClick={handleConvert} disabled={!img || status.tone === 'working'} className={primaryButton}>
                        Convert to {FORMAT_LABEL[to]}
                    </button>
                    <button
                        type='button'
                        disabled={!result || !file}
                        onClick={() => result && file && downloadBlob(result, `${fileStem(file.name)}.${to}`)}
                        className={secondaryButton}
                    >
                        <Download className='size-4' /> Save {FORMAT_LABEL[to]}
                    </button>
                </div>

                <Status tone={status.tone}>{status.text}</Status>
                <p className='text-xs text-slate-500'>Converting cannot bring back detail an earlier save already removed.</p>
                <PrivacyNote />
            </div>

            <div className='space-y-4'>
                <Stats
                    items={[
                        { label: 'Original', value: file ? formatBytes(file.size) : '—' },
                        { label: 'Converted', value: result ? formatBytes(result.size) : '—' },
                        { label: 'Dimensions', value: img ? `${img.naturalWidth} × ${img.naturalHeight}` : '—' },
                        { label: 'New format', value: FORMAT_LABEL[to] }
                    ]}
                />
                <Preview blob={preview} alt='Preview of your image' />
            </div>
        </div>
    )
}

export default ConvertTool
